import React, { useEffect, useState } from "react";
import { useLazyQuery, useMutation } from "@apollo/client";
import {
  GET_PAYMENT_LINK_BOOKING_INFO,
  RESOLVE_UNLINKED_RAZORPAY_PAYMENT,
} from "./graphql";
import EmployeeDepositPicker from "./EmployeeDepositPicker";
import { inr, formatDate, formatDateTime, SUGGESTION_LABELS } from "./format";

const ACTIONS = [
  { key: "LINK_BOOKING", label: "Link to booking" },
  { key: "EMPLOYEE_DEPOSIT", label: "Professional cash deposit" },
  { key: "OTHER_INCOME", label: "Other income" },
  { key: "IGNORE", label: "Ignore" },
];
const OTHER_CATEGORIES = ["Other Income", "Training Fee", "Product Sale"];

const defaultAction = (kind) => {
  if (kind === "EMPLOYEE_CASH_DEPOSIT") return "EMPLOYEE_DEPOSIT";
  if (kind === "TEST") return "IGNORE";
  return "LINK_BOOKING";
};

/**
 * Admin decides what an unlinked Razorpay payment was for. Linking to a
 * booking adds it to the booking's amount paid online (and takes it out of
 * the professional's cash); a deposit settles the professional's cash
 * bookings; other income goes to the payments ledger.
 */
const ResolveUnlinkedPaymentForm = ({ payment, onClose, onResolved }) => {
  const [action, setAction] = useState(defaultAction(payment.suggestedKind));
  const [bookingId, setBookingId] = useState(
    payment.suggestedOrder?.bookingId || "",
  );
  const [employeeId, setEmployeeId] = useState(
    payment.suggestedEmployeeId || null,
  );
  const [orderIds, setOrderIds] = useState([]);
  const [category, setCategory] = useState(OTHER_CATEGORIES[0]);
  const [note, setNote] = useState("");
  const [error, setError] = useState("");

  const [lookupBooking, { data: bookingData, loading: bookingLoading }] =
    useLazyQuery(GET_PAYMENT_LINK_BOOKING_INFO, { fetchPolicy: "network-only" });
  const booking =
    bookingData?.getPaymentLinkBookingInfo?.status === "SUCCESS"
      ? bookingData.getPaymentLinkBookingInfo.booking
      : null;
  const bookingError =
    bookingData?.getPaymentLinkBookingInfo?.status === "ERROR"
      ? bookingData.getPaymentLinkBookingInfo.message
      : "";

  useEffect(() => {
    if (bookingId) lookupBooking({ variables: { bookingId } });
    // only for the suggested booking on open
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [resolve, { loading }] = useMutation(RESOLVE_UNLINKED_RAZORPAY_PAYMENT);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    const data = { id: payment.id, action, note: note.trim() || undefined };
    if (action === "LINK_BOOKING") {
      if (!bookingId.trim()) return setError("Enter the booking ID");
      data.bookingId = bookingId.trim();
    } else if (action === "EMPLOYEE_DEPOSIT") {
      if (!employeeId) return setError("Select the professional");
      if (!orderIds.length) return setError("Select the bookings this deposit settles");
      data.employeeId = employeeId;
      data.orderIds = orderIds;
    } else if (action === "OTHER_INCOME") {
      data.category = category;
    } else if (!note.trim()) {
      return setError("Add a note saying why this is ignored");
    }
    try {
      const res = await resolve({ variables: { data } });
      const out = res.data?.resolveUnlinkedRazorpayPayment;
      if (out?.status !== "SUCCESS") return setError(out?.message || "Failed");
      onResolved?.();
      onClose();
    } catch (err) {
      setError(err.message);
    }
    return null;
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">
          Link Razorpay payment · {inr(payment.amount)}
        </h2>
        <button type="button" onClick={onClose} className="text-gray-500">
          ✕
        </button>
      </div>

      <div className="text-sm bg-gray-50 border rounded-md p-3 grid grid-cols-2 gap-1">
        <span className="text-gray-500">Paid on</span>
        <span>{formatDateTime(payment.paidAt)}</span>
        <span className="text-gray-500">Payer</span>
        <span>
          {payment.contact || "—"}
          {payment.vpa ? ` · ${payment.vpa}` : ""}
        </span>
        <span className="text-gray-500">Link description</span>
        <span>{payment.description || "—"}</span>
        <span className="text-gray-500">Created by</span>
        <span>
          {payment.linkCreatedBy
            ? payment.linkCreatedBy === "API"
              ? "Our app"
              : `Razorpay dashboard (${payment.linkCreatedBy})`
            : "—"}
        </span>
        <span className="text-gray-500">Razorpay payment</span>
        <span className="font-mono text-xs">{payment.razorpayPaymentId}</span>
        {payment.suggestedKind && (
          <>
            <span className="text-gray-500">Suggestion</span>
            <span className="font-medium">
              {SUGGESTION_LABELS[payment.suggestedKind]}
              {payment.suggestedOrder && ` · ${payment.suggestedOrder.bookingId}`}
              {payment.suggestedEmployeeName &&
                ` · ${payment.suggestedEmployeeName}`}
            </span>
          </>
        )}
      </div>

      <div className="grid grid-cols-4 gap-2">
        {ACTIONS.map((a) => (
          <button
            key={a.key}
            type="button"
            onClick={() => {
              setAction(a.key);
              setError("");
            }}
            className={`px-2 py-2 rounded-md text-sm border ${
              action === a.key
                ? "bg-[#FBB033] border-[#FBB033] font-semibold"
                : "bg-white"
            }`}
          >
            {a.label}
          </button>
        ))}
      </div>

      {action === "LINK_BOOKING" && (
        <div className="space-y-2">
          <div className="flex gap-2 items-end">
            <label className="flex-1 block text-sm font-medium text-gray-700">
              Booking ID
              <input
                value={bookingId}
                onChange={(e) => setBookingId(e.target.value)}
                onBlur={() =>
                  bookingId.trim() &&
                  lookupBooking({ variables: { bookingId: bookingId.trim() } })
                }
                className="mt-1 w-full border rounded-md px-3 py-2 font-mono"
              />
            </label>
            <button
              type="button"
              onClick={() =>
                bookingId.trim() &&
                lookupBooking({ variables: { bookingId: bookingId.trim() } })
              }
              className="px-4 py-2 border rounded-md"
            >
              {bookingLoading ? "…" : "Find"}
            </button>
          </div>
          {bookingError && <p className="text-sm text-red-600">{bookingError}</p>}
          {booking && (
            <div className="text-sm border rounded-md p-3 grid grid-cols-2 gap-1">
              <span className="text-gray-500">Customer</span>
              <span>
                {booking.customerName} ({booking.customerContact})
              </span>
              <span className="text-gray-500">Service date / status</span>
              <span>
                {formatDate(booking.bookingDate)} · {booking.status}
              </span>
              <span className="text-gray-500">Price · paid online · type</span>
              <span>
                {inr(booking.price)} · {inr(booking.amountPaid)} ·{" "}
                {booking.paymentType}
              </span>
              <span className="text-gray-500">Not yet paid online</span>
              <span
                className={`font-semibold ${
                  Math.abs(booking.amountDue - payment.amount) > 1
                    ? "text-red-600"
                    : "text-green-700"
                }`}
              >
                {inr(booking.amountDue)}
              </span>
            </div>
          )}
          <p className="text-xs text-gray-500">
            Adds this payment to the booking's amount paid online. For a
            completed booking, the professional's cash for it goes down by the
            same amount.
          </p>
        </div>
      )}

      {action === "EMPLOYEE_DEPOSIT" && (
        <EmployeeDepositPicker
          employeeId={employeeId}
          setEmployeeId={setEmployeeId}
          selectedOrderIds={orderIds}
          setSelectedOrderIds={setOrderIds}
          targetAmount={payment.amount}
        />
      )}

      {action === "OTHER_INCOME" && (
        <label className="block text-sm font-medium text-gray-700">
          Category
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="mt-1 w-full border rounded-md px-3 py-2"
          >
            {OTHER_CATEGORIES.map((c) => (
              <option key={c}>{c}</option>
            ))}
          </select>
        </label>
      )}

      <label className="block text-sm font-medium text-gray-700">
        Note {action === "IGNORE" ? "(required)" : "(optional)"}
        <input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          className="mt-1 w-full border rounded-md px-3 py-2"
        />
      </label>

      {error && <p className="text-sm text-red-600">{error}</p>}

      <div className="flex justify-end gap-2">
        <button type="button" onClick={onClose} className="px-4 py-2 border rounded-md">
          Cancel
        </button>
        <button
          type="submit"
          disabled={loading}
          className="px-4 py-2 bg-[#FBB033] rounded-md font-semibold disabled:opacity-60"
        >
          {loading ? "Saving…" : "Save"}
        </button>
      </div>
    </form>
  );
};

export default ResolveUnlinkedPaymentForm;
